import { useInfiniteQuery } from '@tanstack/react-query';
import isEmpty from 'lodash-es/isEmpty';
import isNil from 'lodash-es/isNil';
import uniq from 'lodash-es/uniq';
import { useMemo } from 'react';
import { useClient } from '../../ClientProvider';
import { OptionCode } from '../../enums';
import { getPageQueryParams } from '../../pagination';
import { plotsOnlySources, sourceToMetric } from '../../resources/alerts.types';
import useQuery from '../../useQuery';
const metricOperator = 'and';
const EMPTY_TARGET_WITH_NAME = [];
const useGetTargetQueriesDetails = ({ sources, q, limit }) => {
    const hasPlotOnlySource = !isEmpty(sources) && sources.some((s) => plotsOnlySources.includes(s));
    const metrics = useMemo(() => sources.map((source) => {
        const metric = sourceToMetric(source);
        // CAPA is not supported for this field
        if (metric === 'CAPA') {
            return 'U_CAPA';
        }
        else {
            return metric;
        }
    }), [sources]);
    const plotsQueryParams = useMemo(() => ({
        isArchived: false,
        metrics,
        metricOperator,
        q: q,
        limit,
        ...(sources.includes('TAVMILLS') ? { availableOptions: [OptionCode.tavmills] } : {})
    }), [metrics, q, limit, sources]);
    const devicesQueryParams = useMemo(() => ({ metrics, metricOperator, q, limit }), [metrics, q, limit]);
    return { hasPlotOnlySource, metrics, plotsQueryParams, devicesQueryParams };
};
const useGetAvailableAlertTargets = ({ sources, q, currentPage = 0, limit = 20, targetKind }) => {
    const client = useClient();
    const { metrics, hasPlotOnlySource, plotsQueryParams, devicesQueryParams } = useGetTargetQueriesDetails({
        sources,
        q: q,
        limit
    });
    const plotsRequest = useQuery(client.plots.getPage(currentPage, plotsQueryParams), {
        enabled: !isEmpty(metrics) && targetKind === 'plots'
    });
    const devicesRequest = useQuery(client.devices.getPage(currentPage, devicesQueryParams), {
        enabled: !isEmpty(metrics) && !hasPlotOnlySource && targetKind === 'devices'
    });
    const stationsRequest = useQuery(client.stations.getPage(currentPage, devicesQueryParams), {
        enabled: !isEmpty(metrics) && !hasPlotOnlySource && targetKind === 'stations'
    });
    const plots = plotsRequest.data?.results;
    const devices = devicesRequest.data?.results;
    const stations = stationsRequest.data?.results;
    // gathering org ids and filtering out the undefined ones, as well as the duplicates
    const plotsOrgIds = useMemo(() => plots ? plots?.filter((p) => !isNil(p.organizationId)).map((p) => p.organizationId) : [], [plots]);
    const devicesOrgIds = useMemo(() => devices ? devices?.filter((d) => !isNil(d.organizationId)).map((d) => d.organizationId) : [], [devices]);
    const stationsOrgIds = useMemo(() => stations
        ? stations?.filter((s) => !isNil(s.organizationId)).map((s) => s.organizationId)
        : [], [stations]);
    const allOrgIds = targetKind === 'plots'
        ? uniq(plotsOrgIds)
        : targetKind === 'devices'
            ? uniq(devicesOrgIds)
            : uniq(stationsOrgIds);
    const orgsQueryParams = useMemo(() => ({ ids: allOrgIds }), [allOrgIds]);
    const orgsRequest = useQuery(client.orgs.getMany(orgsQueryParams), {
        enabled: !isEmpty(allOrgIds)
    });
    const orgs = useMemo(() => orgsRequest.data?.results, [orgsRequest.data]);
    const isLoading = plotsRequest.isLoading ||
        devicesRequest.isLoading ||
        orgsRequest.isLoading ||
        stationsRequest.isLoading;
    const completePlots = useMemo(() => plots
        ? plots.map((plt) => {
            const matchingOrg = orgs?.find((org) => org.id === plt.organizationId);
            return { ...plt, orgName: matchingOrg?.name };
        })
        : EMPTY_TARGET_WITH_NAME, [plots, orgs]);
    const completeDevices = useMemo(() => devices
        ? devices.map((dvc) => {
            const matchingOrg = orgs?.find((org) => org.id === dvc.organizationId);
            return { ...dvc, orgName: matchingOrg?.name };
        })
        : EMPTY_TARGET_WITH_NAME, [devices, orgs]);
    const completeStations = useMemo(() => stations
        ? stations.map((dvc) => {
            const matchingOrg = orgs?.find((org) => org.id === dvc.organizationId);
            return { ...dvc, orgName: matchingOrg?.name };
        })
        : EMPTY_TARGET_WITH_NAME, [stations, orgs]);
    const resourceRequest = targetKind === 'plots'
        ? plotsRequest
        : targetKind === 'devices'
            ? devicesRequest
            : stationsRequest;
    const completeTargets = targetKind === 'plots'
        ? completePlots
        : targetKind === 'devices'
            ? completeDevices
            : completeStations;
    return {
        completeTargets,
        count: resourceRequest.data?.count,
        isLoading,
        hasPrevious: !isNil(resourceRequest.data?.previous),
        hasNext: !isNil(resourceRequest.data?.next),
        hasPlotOnlySource
    };
};
export default useGetAvailableAlertTargets;
export const useGetInfiniteAvailableAlertTargets = ({ sources, q, limit = 20, targetKind }) => {
    const client = useClient();
    const { metrics, hasPlotOnlySource, plotsQueryParams, devicesQueryParams } = useGetTargetQueriesDetails({
        sources,
        q: q,
        limit
    });
    //force update queryKey to trigger refetch on q change
    const queryKey = [`${`${targetKind}-infinite-target-list`}_${q}`];
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    //@ts-ignore
    const requester = targetKind === 'plots'
        ? client.plots.getMany
        : targetKind === 'devices'
            ? client.devices.getMany
            : client.stations.getMany;
    const queryParams = targetKind === 'plots' ? plotsQueryParams : devicesQueryParams;
    const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, isFetching, refetch } = useInfiniteQuery({
        queryKey,
        queryFn: ({ pageParam }) => requester({
            ...queryParams,
            ...(pageParam || {})
        }).request(),
        initialPageParam: queryParams,
        enabled: !isEmpty(metrics),
        getNextPageParam: (lastPage, allPages) => {
            return !isNil(lastPage.next)
                ? { ...queryParams, ...getPageQueryParams(allPages.length, limit) }
                : undefined;
        }
    });
    const rawTargets = useMemo(() => data
        ? data.pages.reduce((acc, dataPage) => acc.concat(dataPage.results ?? []), [])
        : [], [data]);
    // gathering org ids and filtering out the undefined ones, as well as the duplicates
    const orgsIds = useMemo(() => rawTargets
        ? rawTargets?.filter((p) => !isNil(p.organizationId)).map((p) => p.organizationId)
        : [], [rawTargets]);
    const allOrgIds = uniq(orgsIds);
    const orgsQueryParams = useMemo(() => ({ ids: allOrgIds }), [allOrgIds]);
    const orgsRequest = useQuery(client.orgs.getMany(orgsQueryParams), {
        enabled: !isEmpty(allOrgIds)
    });
    const orgs = useMemo(() => orgsRequest.data?.results, [orgsRequest.data]);
    const completeTargets = useMemo(() => rawTargets
        ? rawTargets.map((tgt) => {
            const matchingOrg = orgs?.find((org) => org.id === tgt.organizationId);
            return { ...tgt, orgName: matchingOrg?.name };
        })
        : [], [rawTargets, orgs]);
    const fetchNext = () => {
        if (!isLoading && !isFetching && !isFetchingNextPage && hasNextPage) {
            fetchNextPage();
        }
    };
    return {
        completeTargets,
        fetchNext,
        hasPlotOnlySource,
        isFetching,
        isLoading,
        refetch
    };
};
